import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import api from "@/lib/api";
import Breadcrumbs from "@/components/admin/Breadcrumbs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { formatApiError } from "@/lib/errors";

export default function PlatformAudit() {
  const [tenants, setTenants] = useState([]);
  const [selected, setSelected] = useState([]);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => { api.get("/platform/tenants").then(r => setTenants(r.data)); }, []);

  const buildFilterParams = () => {
    const p = new URLSearchParams();
    if (selected.length) p.set("tenant_ids", selected.join(","));
    if (q) p.set("q", q);
    return p;
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = buildFilterParams();
      params.set("limit", "50");
      const { data } = await api.get(`/platform/audit?${params}`);
      setRows(data.rows); setTotal(data.total);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [selected.join(","), q]);

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = buildFilterParams();
      const res = await api.get(`/platform/audit/export?${params}`, { responseType: "blob" });
      const rowCount = res.headers?.["x-row-count"] || "?";
      // Filename from Content-Disposition
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/i.exec(cd);
      const filename = m?.[1] || `blackscott-audit-${new Date().toISOString().slice(0, 10)}.csv`;
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${rowCount} rows`);
      // Refresh list so the tenant.audit.export row becomes visible.
      load();
    } catch (err) {
      toast.error(formatApiError(err) || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="platform-audit" className="mx-auto w-full max-w-6xl space-y-5">
      <Breadcrumbs />
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold text-slate-900">Cross-tenant audit</h2>
          <p className="mt-1 text-sm text-slate-500">{total} rows match current filter.</p>
        </div>
        <Button
          onClick={exportCsv}
          disabled={exporting || loading}
          data-testid="audit-export-csv"
          className="gap-2 bg-slate-900 hover:bg-slate-800"
        >
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          {exporting ? "Exporting…" : "Export CSV"}
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Search action, actor, target…" value={q} onChange={e => setQ(e.target.value)} className="max-w-sm" data-testid="audit-search" />
        <Button variant="outline" size="sm" onClick={() => setSelected([])}>All tenants</Button>
        {tenants.map(t => (
          <button key={t.id} onClick={() => toggle(t.id)} data-testid={`audit-filter-${t.slug}`}
                  className={`rounded-full px-2 py-1 text-xs font-medium ${selected.includes(t.id) ? "text-white" : "bg-slate-100 text-slate-700"}`}
                  style={{ background: selected.includes(t.id) ? (t.branding?.primary_color || "#0f172a") : undefined }}>
            {t.name}
          </button>
        ))}
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr><th className="px-3 py-2 text-left">Time</th><th className="px-3 py-2 text-left">Tenant</th><th className="px-3 py-2 text-left">Action</th><th className="px-3 py-2 text-left">Actor</th><th className="px-3 py-2 text-left">Target</th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="p-6 text-center text-slate-400">Loading…</td></tr>}
            {rows.map(r => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{new Date(r.ts).toLocaleString()}</td>
                <td className="px-3 py-2">
                  {r.tenant_pill ? (
                    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold text-white" style={{ background: r.tenant_pill.primary_color }}>{r.tenant_pill.name}</span>
                  ) : <span className="text-[10px] text-slate-400">—</span>}
                </td>
                <td className="px-3 py-2 font-mono text-xs text-slate-700">{r.action}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{r.actor_email || "—"}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{r.target_label || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
